// ============================================================================
// EduPath Sprint 5 - Offline Queue Service (IndexedDB)
// ============================================================================
// Persistent queue for offline assessment submissions.
// Uses IndexedDB for reliable storage that survives page reloads and app restarts.
// Falls back to localStorage if IndexedDB is unavailable.
// ============================================================================

import type {
  OfflineQueueItem,
  QueueItemStatus,
  AssessmentResponsePayload,
  OfflineSessionMeta,
} from '@/types/offline';

const DB_NAME = 'edupath-offline';
const DB_VERSION = 5;
const STORE_NAME = 'submission-queue';

// ─── IndexedDB Helpers ───────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // Create submission-queue store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('queuedAt', 'queuedAt', { unique: false });
        store.createIndex('nextRetryAt', 'nextRetryAt', { unique: false });
        store.createIndex('learnerId', 'learnerId', { unique: false });
      }
      // Create stores needed by offlineStore.ts
      if (!db.objectStoreNames.contains('questionBanks')) {
        db.createObjectStore('questionBanks', { keyPath: 'assessmentType' });
      }
      if (!db.objectStoreNames.contains('offlineSessions')) {
        const ss = db.createObjectStore('offlineSessions', { keyPath: 'sessionId' });
        ss.createIndex('learnerId', 'learnerId', { unique: false });
        ss.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains('syncQueue')) {
        const sq = db.createObjectStore('syncQueue', { keyPath: 'id' });
        sq.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains('checkpoints')) {
        db.createObjectStore('checkpoints', { keyPath: 'sessionId' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function txOperation<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const request = operation(store);

      request.onsuccess = () => {
        resolve(request.result);
        db.close();
      };
      request.onerror = () => {
        reject(request.error);
        db.close();
      };
    } catch (err) {
      reject(err);
    }
  });
}

function txGetAll(
  indexName?: string,
  query?: IDBValidKey | IDBKeyRange
): Promise<OfflineQueueItem[]> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const target = indexName ? store.index(indexName) : store;
      const request = query ? target.getAll(query) : target.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
        db.close();
      };
      request.onerror = () => {
        reject(request.error);
        db.close();
      };
    } catch (err) {
      reject(err);
    }
  });
}

// ─── UUID Generator ──────────────────────────────────────────────────────────

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const offlineQueue = {
  /**
   * Enqueue a completed assessment for later sync.
   * Called when the user finishes an assessment while offline (or as standard flow).
   */
  async enqueue(params: {
    sessionId: string;
    learnerId: string;
    assessmentType: string;
    idempotencyKey: string;
    responses: AssessmentResponsePayload[];
    sessionMeta: OfflineSessionMeta;
  }): Promise<OfflineQueueItem> {
    // Check for duplicate (same session already queued)
    const existing = await this.getBySessionId(params.sessionId);
    if (existing && existing.status !== 'REJECTED' && existing.status !== 'SYNCED') {
      console.warn(`[OfflineQueue] Session ${params.sessionId} already queued, skipping`);
      return existing;
    }

    const item: OfflineQueueItem = {
      id: generateId(),
      sessionId: params.sessionId,
      learnerId: params.learnerId,
      assessmentType: params.assessmentType,
      idempotencyKey: params.idempotencyKey,
      responses: params.responses,
      sessionMeta: params.sessionMeta,
      status: 'PENDING',
      retryCount: 0,
      maxRetries: 5,
      queuedAt: new Date().toISOString(),
      lastAttemptAt: null,
      nextRetryAt: null,
      errorMessage: null,
      rejectCode: null,
      integrityFlags: [],
      serverResponse: null,
    };

    await txOperation('readwrite', (store) => store.put(item));
    console.log(`[OfflineQueue] Enqueued submission for session ${params.sessionId}`);
    return item;
  },

  /**
   * Get a single queue item by ID.
   */
  async getById(id: string): Promise<OfflineQueueItem | null> {
    try {
      const result = await txOperation<OfflineQueueItem | undefined>(
        'readonly',
        (store) => store.get(id)
      );
      return result || null;
    } catch {
      return null;
    }
  },

  /**
   * Get queue item by session ID.
   */
  async getBySessionId(sessionId: string): Promise<OfflineQueueItem | null> {
    const items = await txGetAll('sessionId', sessionId);
    // Return the most recent non-synced item
    const active = items.filter((i) => i.status !== 'SYNCED');
    return active.length > 0 ? active[active.length - 1] : null;
  },

  /**
   * Get all items in the queue.
   */
  async getAll(): Promise<OfflineQueueItem[]> {
    return txGetAll();
  },

  /**
   * Get items by status.
   */
  async getByStatus(status: QueueItemStatus): Promise<OfflineQueueItem[]> {
    return txGetAll('status', status);
  },

  /**
   * Get items ready to sync: PENDING or FAILED with nextRetryAt <= now.
   */
  async getReadyToSync(): Promise<OfflineQueueItem[]> {
    const all = await this.getAll();
    const now = new Date().toISOString();

    return all.filter((item) => {
      if (item.status === 'PENDING') return true;
      if (item.status === 'FAILED' && item.retryCount < item.maxRetries) {
        if (!item.nextRetryAt) return true;
        return item.nextRetryAt <= now;
      }
      return false;
    });
  },

  /**
   * Update a queue item's status and metadata.
   */
  async updateItem(
    id: string,
    updates: Partial<OfflineQueueItem>
  ): Promise<OfflineQueueItem | null> {
    const existing = await this.getById(id);
    if (!existing) return null;

    const updated = { ...existing, ...updates };
    await txOperation('readwrite', (store) => store.put(updated));
    return updated;
  },

  /**
   * Mark an item as syncing (in-flight).
   */
  async markSyncing(id: string): Promise<OfflineQueueItem | null> {
    return this.updateItem(id, {
      status: 'SYNCING',
      lastAttemptAt: new Date().toISOString(),
    });
  },

  /**
   * Mark an item as successfully synced.
   */
  async markSynced(
    id: string,
    serverResponse?: Record<string, unknown>
  ): Promise<OfflineQueueItem | null> {
    return this.updateItem(id, {
      status: 'SYNCED',
      serverResponse: serverResponse || null,
      errorMessage: null,
    });
  },

  /**
   * Mark an item as failed with exponential backoff.
   */
  async markFailed(
    id: string,
    errorMessage: string
  ): Promise<OfflineQueueItem | null> {
    const existing = await this.getById(id);
    if (!existing) return null;

    const newRetryCount = existing.retryCount + 1;
    // Exponential backoff: 30s, 2m, 8m, 32m, 2h
    const backoffMs = Math.min(30000 * Math.pow(4, newRetryCount - 1), 7200000);
    const nextRetry = new Date(Date.now() + backoffMs).toISOString();

    const isFinalFailure = newRetryCount >= existing.maxRetries;

    return this.updateItem(id, {
      status: isFinalFailure ? 'REJECTED' : 'FAILED',
      retryCount: newRetryCount,
      nextRetryAt: isFinalFailure ? null : nextRetry,
      errorMessage,
      rejectCode: isFinalFailure ? 'VALIDATION_ERROR' : existing.rejectCode,
    });
  },

  /**
   * Mark an item as server-rejected (no retry).
   */
  async markRejected(
    id: string,
    rejectCode: string,
    errorMessage: string,
    integrityFlags?: string[]
  ): Promise<OfflineQueueItem | null> {
    return this.updateItem(id, {
      status: 'REJECTED',
      rejectCode: rejectCode as OfflineQueueItem['rejectCode'],
      errorMessage,
      integrityFlags: (integrityFlags || []) as OfflineQueueItem['integrityFlags'],
      nextRetryAt: null,
    });
  },

  /**
   * Mark item as needing conflict resolution.
   */
  async markConflict(
    id: string,
    errorMessage: string,
    serverResponse?: Record<string, unknown>
  ): Promise<OfflineQueueItem | null> {
    return this.updateItem(id, {
      status: 'CONFLICT',
      errorMessage,
      serverResponse: serverResponse || null,
    });
  },

  /**
   * Remove a single item from the queue.
   */
  async remove(id: string): Promise<void> {
    await txOperation('readwrite', (store) => store.delete(id));
  },

  /**
   * Remove all synced items (cleanup after successful sync).
   */
  async clearSynced(): Promise<number> {
    const synced = await this.getByStatus('SYNCED');
    for (const item of synced) {
      await this.remove(item.id);
    }
    return synced.length;
  },

  /**
   * Remove all items (full reset).
   */
  async clearAll(): Promise<void> {
    const all = await this.getAll();
    for (const item of all) {
      await this.remove(item.id);
    }
  },

  /**
   * Get queue statistics for UI display.
   */
  async getStats(): Promise<{
    total: number;
    pending: number;
    syncing: number;
    synced: number;
    failed: number;
    rejected: number;
    conflict: number;
  }> {
    const all = await this.getAll();
    return {
      total: all.length,
      pending: all.filter((i) => i.status === 'PENDING').length,
      syncing: all.filter((i) => i.status === 'SYNCING').length,
      synced: all.filter((i) => i.status === 'SYNCED').length,
      failed: all.filter((i) => i.status === 'FAILED').length,
      rejected: all.filter((i) => i.status === 'REJECTED').length,
      conflict: all.filter((i) => i.status === 'CONFLICT').length,
    };
  },

  /**
   * Check if IndexedDB is available.
   */
  isAvailable(): boolean {
    try {
      return typeof indexedDB !== 'undefined';
    } catch {
      return false;
    }
  },
};

export default offlineQueue;
